import { isSafeDestination } from "@anthra/platform/session"
import { Loading } from "@anthra/ui/feedback"
import { SIGN_IN_COPY } from "@anthra/ui/phone-number-input"
import { SignInSplit } from "@anthra/ui/sign-in-hero"
import { Description, Meta, SectionTitle } from "@anthra/ui/typography"
import { createFileRoute, useNavigate, useRouter } from "@tanstack/react-router"
import { useEffect } from "react"

import { PublicFooter } from "@/components/public-page"
import { Topbar } from "@/components/topbar"
import { CodeStep } from "@/features/auth/code-step"
import { NumberStep } from "@/features/auth/number-step"
import { RegistrationStep } from "@/features/auth/registration-step"
import { useSession } from "@/features/auth/use-auth"
import { entryPhase, useEntryStore } from "@/stores/use-entry-store"
import { useSessionStore } from "@/stores/use-session-store"

/**
 * The single entry screen (US-00-001).
 *
 * ONE ROUTE, THREE PHASES. There is no separate registration page reached by
 * navigating: a number is typed, and whether the profile fields appear is the
 * server's answer to the lookup rather than a route the user chose
 * (US-00-001.5/.6). The "details" phase renders `screens/registration.html`'s
 * own full layout rather than the welcome-signin card — a phase change, not a
 * navigation, so no URL changes and nothing is lost by it.
 *
 * On success the person lands on the destination they were originally trying to
 * reach — a WhatsApp deep link, or the screen a lapsed session interrupted — and
 * that destination is DISCARDED AFTER ONE USE (US-00-001.20, US-00-002.21/.26).
 *
 * THE MARKUP IS THE DESIGN'S. Class names, nesting and order match
 * screens/welcome-signin.html, and entry.css is that screen's own rules rather
 * than a Tailwind re-expression of them. Utilities are what let this screen
 * drift into a different product — a teal card in system-ui against a design in
 * maroon and Source Serif 4 — with nothing mechanical to notice it.
 */
/** The sign-in photograph, served from this app's public folder. */
const SIGN_IN_HERO_SRC = `${import.meta.env.BASE_URL}images/sign-in-hero.webp`

export const Route = createFileRoute("/entry")({
	component: EntryScreen,
})

function EntryScreen() {
	const navigate = useNavigate()
	const router = useRouter()
	const phase = useEntryStore(entryPhase)
	const consumeDestination = useSessionStore(state => state.consumeDestination)
	const sessionStatus = useSessionStore(state => state.status)
	// The passive session probe. A user who is already signed in and lands on
	// /entry (by typing it in the address bar, following a stale bookmark, or
	// using browser back after sign-in) must be sent to Home — the store's
	// `status` is only flipped by the OTP verify mutation in this tab, so a
	// live cookie alone would otherwise leave them staring at the sign-in card.
	const session = useSession()

	// The entry flow's state lives exactly as long as this screen does.
	//
	// It used to be cleared by the verify mutation's onSuccess, which unmounted
	// <CodeStep> before React Query could call the mutate()-level onSuccess that
	// navigates — so a correct code left the person on the sign-in screen. Doing
	// it on unmount clears the same state and cannot pre-empt the navigation,
	// and it now also covers leaving the flow by the back button or a link.
	useEffect(() => () => useEntryStore.getState().reset(), [])

	// TWO PATHS TO HOME, ONE EFFECT.
	//
	// (a) OTP verify succeeds in this tab. `useVerifyOtp`'s hook-level
	// `onSuccess` flips `useSessionStore.status` to "authenticated" — the
	// mutate()-level onSuccess is silently skipped when TanStack Query's
	// MutationObserver has no listeners (which a real browser can arrange
	// between request settlement and the next commit; see TC-US-00-001-1-1
	// step 4, TC-US-00-001-1-3 step 2, TC-US-00-001-2-2 step 2), so the store
	// flip is what this reacts to instead.
	//
	// (b) A live cookie is already in place — the /me probe returned a user.
	// The store field never gets set on this path, because nothing in this tab
	// verified an OTP; the query cache is the only signal.
	//
	// AN EXPLICIT `signed_out` BEATS THE PROBE, and has to. `useSignOut` clears
	// the query cache and marks the store signed out, so the very next render
	// here refetches /me — and if that call has not yet stopped answering with
	// a user (the sign-out request itself failed, or the cookie outlives it)
	// this would send the person to /home, whose guard sends them straight back
	// here, forever. The store is the only thing that knows a sign-out was
	// asked for in this tab.
	useEffect(() => {
		const isAuthed =
			sessionStatus === "authenticated" || (sessionStatus !== "signed_out" && !!session.data)
		if (!isAuthed) return
		const destination = consumeDestination()
		// `history.push` rather than `navigate({ to })`: the destination is a
		// runtime string held across the OTP round trip, not one of the router's
		// statically-known paths. `isSafeDestination` keeps it same-origin —
		// an open redirect is the classic way a held destination is abused.
		if (destination !== null && isSafeDestination(destination)) {
			router.history.push(destination)
			return
		}
		void navigate({ to: "/home", replace: true })
	}, [sessionStatus, session.data, consumeDestination, router, navigate])

	// Don't flash the sign-in card while /me is still in flight, and don't
	// render it for the split second between `session.data` arriving and the
	// effect above committing the navigation. Same `signed_out` exception: once
	// a sign-out has been asked for, this screen is the destination and must
	// render whatever the probe still says.
	if (sessionStatus !== "signed_out") {
		if (session.isPending) return <Loading label="Checking your session…" />
		if (session.data) return null
	}

	// The registration details step is the design's own full screen, not a
	// handful of fields inside welcome-signin's card — see registration-step.tsx.
	if (phase === "details") {
		return <RegistrationStep />
	}

	// The OTP step is ALSO the design's own full screen — screens/otp-verification.html's
	// `.app`/`.split`/`.card`+`.rail`, not welcome-signin's pitch-and-panel — so it
	// gets its own top-level layout rather than being folded into <main class="entry">.
	if (phase === "code") {
		return <OtpScreen />
	}

	return (
		<SignInSplit
			heroSrc={SIGN_IN_HERO_SRC}
			header={<Topbar current="entry" />}
			footer={<PublicFooter />}
		>
			{/*
			 * ONE CENTRED COLUMN. The pitch and feature list that used to sit
			 * beside the card were replaced by the photograph, which now fills
			 * the left of a wide screen as a framed picture (SignInSplit). What is
			 * left is the card beside it, and on a phone the whole screen.
			 */}
			<main className="mx-auto flex w-full max-w-[36rem] flex-1 flex-col justify-center gap-6 px-[var(--space-gutter-mobile)] py-8 md:px-[var(--space-gutter-tablet)] lg:max-w-none lg:px-0 lg:py-0">
				{/* The links sit inside the card, centred under Send OTP, so the
				    card is the whole of the right half, as in the design. */}
				<section className="animate-rise-in border-border bg-card flex flex-col rounded-[1rem] border p-8 shadow-[0_4px_24px_rgba(0,0,0,0.03)] sm:p-10 lg:flex-1 xl:p-12">
					<SectionTitle
						as="h2"
						className="mb-4 text-[1.125rem] leading-snug font-bold tracking-tight text-balance sm:mb-6 sm:text-[1.25rem] xl:text-[1.375rem] min-[96rem]:text-[1.5rem]"
					>
						Sign in with your WhatsApp number
					</SectionTitle>

					<NumberStep />

					<div
						className="animate-rise-in mt-6 flex flex-col items-center gap-2 text-center [&>p]:text-xs"
						style={{ animationDelay: "300ms" }}
					>
						<Meta>
							<a
								href="https://www.swamithra.com"
								target="_blank"
								rel="noopener noreferrer"
								className="text-link hover:text-link-hover underline"
							>
								Swamithra serves you without any cost
							</a>
						</Meta>
						<Meta>
							By continuing you agree to our{" "}
							<a
								className="text-link hover:text-link-hover underline"
								href="/terms"
							>
								Terms of service
							</a>{" "}
							and{" "}
							<a
								className="text-link hover:text-link-hover underline"
								href="/privacy"
							>
								Privacy notice
							</a>
							.
						</Meta>
					</div>
				</section>
			</main>
		</SignInSplit>
	)
}

/**
 * The OTP step's own screen — screens/otp-verification.html's `.app`, with
 * <CodeStep> supplying the `.card`. The rail's four facts and its legal line
 * are fixed copy the design carries on every state, so they live here rather
 * than being threaded through the step component.
 */
function OtpScreen() {
	return (
		<SignInSplit
			heroSrc={SIGN_IN_HERO_SRC}
			header={<Topbar />}
			footer={<PublicFooter />}
		>
			<main className="mx-auto flex w-full max-w-[36rem] flex-1 flex-col justify-center gap-8 px-[var(--space-gutter-mobile)] py-8 md:px-[var(--space-gutter-tablet)] lg:max-w-none lg:px-0 lg:py-0">
				{/* The same centred 36rem column as the sign-in card on step 1, so
				    the card keeps its place and width between the two steps. */}
				<div className="animate-rise-in border-border bg-card flex min-w-0 flex-col gap-5 rounded-[1rem] border p-8 shadow-[0_4px_24px_rgba(0,0,0,0.03)] sm:p-10 lg:flex-1 xl:p-12">
					<div className="flex flex-col gap-1">
						<SectionTitle
							as="h1"
							className="text-[1.125rem] leading-snug font-bold tracking-tight text-balance sm:text-[1.25rem] xl:text-[1.375rem] min-[96rem]:text-[1.5rem]"
						>
							{SIGN_IN_COPY.codeTitle}
						</SectionTitle>
						<Description>{SIGN_IN_COPY.codeDescription}</Description>
					</div>
					<CodeStep />
				</div>
			</main>
		</SignInSplit>
	)
}
